import React from 'react';
import { useQuery } from "react-query";
import { format } from 'date-fns';
import { apiURL } from "../shared/api/variables";
import { fetchClient } from "../shared/api/fetching";
import ErrorDisplay from "../shared/components/ErrorDisplay";
import LoadingDisplay from "../shared/components/LoadingDisplay";
import ToggleButton from "../shared/components/ToggleButton";
import TicketmasterIcon from "../shared/svg/TicketmasterIcon";
import Picture from "../shared/components/Picture";
import { statusTypes } from "../mls-match-list/utils";
import { formatImageUrl, getCultureLocalizer } from "../shared/utils";
import { useReactAppState } from "../shared/containers/utils";
import { useMatchStatus } from "../shared/hooks/useMatchStatus";
import { trackMatchHubClick } from '../../libraries/_modules/tracking/helpers';
import dayjs from 'dayjs';
import { TICKET_MASTER_PREFIX } from './utils';
import d3endpoints from "../shared/api/d3endpoints";
import useMLSMatch from '../mls-match-list/hooks/useMLSMatch';

function TicketingBlock({ options }) {
  const { match: appMatch, mediaQueries, t, matchStatusOverride, urlList } = useReactAppState();
  const { ticketMasterPostMatchUrl } = urlList || {};
  const { isMediumLargeMin } = mediaQueries || {};
  const matchSportecId = options.matchSportecId || appMatch?.sportecId;

  const { status: matchStatus, queryConfig, isLoading } = useMatchStatus({
    initialState: {
      matchSportecId
    }
  });

  const status = matchStatusOverride?.abbreviation ? matchStatusOverride : matchStatus;

  const d3MatchEndpoint = d3endpoints.getMatchesByMatchId.replace('{matchId}', matchSportecId);

  const {
    isLoading: isLoadingMatch,
    isError: isErrorMatch,
    data: match,
    error: matchError
  } = useQuery({
    queryKey: [`d3-match`, { matchSportecId }],
    queryFn: () => fetchClient({ endpoint: d3MatchEndpoint, apiURL: apiURL.d3SportsAPI }),
    enabled: !!matchSportecId,
    ...queryConfig
  });

  const { isLoading: isLoadingMlsMatch, data: liveData } = useMLSMatch({ matchSportecId });

  const { thirdPartyTickets, firstPartyTickets, slug, competition, isTimeTbd } = match || {};
  const { home, away, environment, match_information } = liveData || {};

  const { logoColorUrl: homeLogoColorUrl } = match?.home || {};
  const { logoColorUrl: awayLogoColorUrl } = match?.away || {};

  const ticketMasterTickets = firstPartyTickets?.url.includes(TICKET_MASTER_PREFIX)
    ? firstPartyTickets
    : thirdPartyTickets?.url.includes(TICKET_MASTER_PREFIX)
      ? thirdPartyTickets
      : null;

  const isTicketMasterPrimarySeller = firstPartyTickets?.url.includes(TICKET_MASTER_PREFIX);
  const isTicketMasterSecondarySeller = thirdPartyTickets?.url.includes(TICKET_MASTER_PREFIX);

  const { stadium_name: name } = environment || {};
  const { team_name: homeFullName, team_short_name: homeShortName } = home || {};
  const { team_name: awayFullName, team_short_name: awayShortName } = away || {};
  const { planned_kickoff_time: matchDate } = match_information || {};

  const localTime = dayjs(matchDate).toDate();
  const dateFormat = isMediumLargeMin ? `EEEE, MMMM do 'at' p` : `E, MMM do 'at' p`;
  const dateTbdFormat = isMediumLargeMin ? `EEEE, MMMM do 'at' ` : `E, MMM do 'at' `;

  const dateTimeDisplay = isTimeTbd
    ? `${localTime ? format(localTime, dateTbdFormat, { locale: getCultureLocalizer() }) : ""}${t("tbd")}`
    : localTime
      ? format(localTime, dateFormat, { locale: getCultureLocalizer() })
      : "";

  const { highQuality: homeClubLogoSrc } = homeLogoColorUrl ? formatImageUrl(homeLogoColorUrl, { format: "w_128,h_128,c_pad", extension: "f_png" }) : {};
  const { highQuality: awayClubLogoSrc } = awayLogoColorUrl ? formatImageUrl(awayLogoColorUrl, { format: "w_128,h_128,c_pad", extension: "f_png" }) : {};

  const availableHomeShortName = homeShortName ? homeShortName : homeFullName;
  const availableAwayShortName = awayShortName ? awayShortName : awayFullName;

  const handleClick = () => {
    trackMatchHubClick({
      clickType: 'ticketing',
      clickItem: 'ticketing_buy_tickets',
      match: slug,
      matchState: status?.abbreviation,
      competition: competition.shortName,
    });
  };

  if (!matchSportecId && !options.isOverridePeriodInArticle) {
    return null;
  }

  const NotPostMatch = ({ tickets }) => {
    if (tickets?.url) {
      // Pre-match
      const replacementRid = options?.source === "Story"
        ? isTicketMasterPrimarySeller
          ? "13" // Story, Pre-match, Primary
          : isTicketMasterSecondarySeller
            ? "14" // Story, Pre-match, Secondary
            : null
        // Source === "MatchHub"
        : isTicketMasterPrimarySeller
          ? "16" // Match Hub, Pre-match, Primary
          : isTicketMasterSecondarySeller
            ? "17" // Match Hub, Pre-match, Secondary
            : null;

      if (replacementRid) {
        tickets.url = tickets.url.replace(/&rid=([0-9]+)/, "&rid=" + replacementRid);
      }
    }

    return (
      tickets?.url
        ? (
          <div role="region" className={`mls-o-ticketing-block`}>
            <div className="mls-o-ticketing-block__ticket-info">
              <div className="mls-o-ticketing-block__club-logo">
                <Picture src={homeClubLogoSrc} title={t("home_club")} />
              </div>
              <div className="mls-o-ticketing-block__match-details">
                <div className="mls-o-ticketing-block__versus">{`${availableHomeShortName} vs. ${availableAwayShortName}`}</div>
                <div className="mls-o-ticketing-block__match-info">
                  <div className="mls-o-ticketing-block__date-time">{dateTimeDisplay}</div>
                  <div className="mls-o-ticketing-block__venue">{name}</div>
                </div>
              </div>
              <div className="mls-o-ticketing-block__club-logo">
                <Picture src={awayClubLogoSrc} title={t("away_club")} />
              </div>
            </div>
            <div className="mls-o-ticketing-block__cta-group">
              <ToggleButton
                target={`${tickets?.openInNewTab ? "_blank" : "_self"}`}
                rel={`${tickets?.openInNewTab ? "noopener noreferrer" : ""}`}
                alt={`${tickets?.accessibleText ? tickets.accessibleText : ""}`}
                href={`${tickets?.url ? tickets.url : "#"}`}
                onClick={handleClick}
                className="mls-o-buttons__basic mls-o-ticketing-block__cta mls-o-ticketing-block__cta--third">
                <span className="mls-o-ticketing-block__cta-label">{t("ticketing_buy_tickets")}</span>
              </ToggleButton>
              <div className="mls-o-ticketing-block__ticket-sponsor">
                <TicketmasterIcon iconType="full" extraClasses={"mls-o-ticketing-block__third-party"} />
              </div>
              <div className="mls-o-ticketing-block__ticketing-partner-label">{t("ticketing_partner")}</div>
            </div>
          </div>
        ) : null
    );
  };

  const PostMatch = ({ ticketMasterPostMatchUrl }) => {
    if (ticketMasterPostMatchUrl && (isTicketMasterPrimarySeller || isTicketMasterSecondarySeller)) {
      const replacementRid = options?.source === "Story"
        ? "15" // Story, Post-match, Primary & Secondary
        : "18"; // Match Hub, Post-match, Primary & Secondary

      ticketMasterPostMatchUrl = ticketMasterPostMatchUrl.replace(/&rid=([0-9]+)/, "&rid=" + replacementRid);

      return (
        ticketMasterPostMatchUrl
          ? (
            <div role="region" className={`mls-o-ticketing-block`}>
              <div className="mls-o-ticketing-block__ticket-info">
                <div className="mls-o-ticketing-block__post-match-details">Ticketmaster has great deals for future MLS matches</div>
              </div>
              <div className="mls-o-ticketing-block__cta-group">
                <ToggleButton
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  alt={`MLS Ticketmaster`}
                  href={ticketMasterPostMatchUrl}
                  onClick={handleClick}
                  className="mls-o-buttons__basic mls-o-ticketing-block__cta mls-o-ticketing-block__cta--third">
                  <span className="mls-o-ticketing-block__cta-label">{t("ticketing_buy_tickets")}</span>
                </ToggleButton>
                <div className="mls-o-ticketing-block__ticket-sponsor">
                  <TicketmasterIcon iconType="full" extraClasses={"mls-o-ticketing-block__third-party"} />
                </div>
                <div className="mls-o-ticketing-block__ticketing-partner-label">{t("ticketing_partner")}</div>
              </div>
            </div>
          ) : null
      );
    }

    return null;
  };

  return (
    isLoadingMatch || isLoadingMlsMatch || isLoading
      ? (
        <div role="region" className={`mls-o-ticketing-block`}>
          <LoadingDisplay />
        </div>
      )
      : isErrorMatch || matchError
        ? (
          <div role="region" className={`mls-o-ticketing-block`}>
            <ErrorDisplay error={matchError} />
          </div>
        )
        : (status?.abbreviation !== statusTypes.pre && status?.abbreviation !== statusTypes.live) || options.isOverridePeriodInArticle
          ? <PostMatch ticketMasterPostMatchUrl={ticketMasterPostMatchUrl} />
          : <NotPostMatch tickets={ticketMasterTickets} />
  );
}

// eslint-disable-next-line no-func-assign
TicketingBlock = React.memo(TicketingBlock);

export default TicketingBlock;
